import auth from "../stores/auth";

export type MbaReadiness = {
  channel: "whatsapp";
  connected: boolean;
  phone_number_id?: string;
  local: { configured: boolean; agent_id?: string; feature_enabled: boolean; sync_enabled: boolean; sync_status: string };
  remote: { is_eligible?: boolean; status?: string; code?: string };
  authorization: { management_writes: "root_super_admin_or_administrator" };
};
export type MbaFaq = { id?: string; question: string; answer: string };
export type MbaKnowledge = { faqs: MbaFaq[]; supported_source: "workspace_faq" };
export type MbaSettings = {
  agent_id?: string;
  channel?: string;
  rollout?: { enabled: boolean };
  handoff?: { enabled: boolean; message?: string };
  followup?: { enabled: boolean; followup_interval_in_seconds?: number; message?: string };
  ai_audience?: "ALLOWLISTED_ONLY" | "EVERYONE";
};
export type MbaAllowlistEntry = { id: string; consumer_phone_number: string };
export type MbaKnowledgePreviewItem = {
  id: string;
  question: string;
  answer: string;
  status: "create" | "update" | "unchanged" | "conflict" | "unsupported";
  unsupported_reason?: string;
};
export type MbaKnowledgePreview = { items: MbaKnowledgePreviewItem[]; counts: Record<string, number> };
export type MbaAgentTestResult = {
  agent_response?: string;
  conversation_id?: string;
  quick_replies?: string[];
  handoff_reason?: string;
  no_response_reason?: string;
  product_variant_ids?: string[];
  estimated_token_usage?: number;
};
export type MbaBusinessInfo = {
  business_description?: string;
  purchase_info?: string;
  payment_method?: string;
  return_policy?: string;
  delivery_and_shipping?: string;
  contact_info?: { email?: string; hours_of_operation?: string; address?: string };
};
export type MbaWebsite = {
  id: string;
  url: string;
  crawl_status?: string;
  pages_crawled?: number;
  last_crawled_at?: string;
};
export type MbaFile = { id: string; name?: string; filename?: string; status?: string; created_at?: string };
export type MbaSkillComponent = "carousel_quick_reply" | "carousel_url" | "cta_url" | "flow" | "image" | "interactive_list" | "location" | "location_request";
export type MbaSkill = {
  id: string;
  title: string;
  component_type: MbaSkillComponent;
  status: "enabled" | "disabled";
  instruction: string;
  flow_id?: number;
};
export type MbaSkillInput = Omit<MbaSkill, "id">;
export type MbaBotCredentials = { id: number; key?: string; password?: string };
export type MbaManagementAccess = { allowed: true; user_id: string; user_type: number };
export type MbaFaqSync = {
  enabled: boolean;
  last_sync_at?: string;
  items: { id: number; source_id: string; status: string; attempts: number; last_error_code?: string; updated_at: string }[];
  history: { id: number; source_id: string; status: string; error_code?: string; created_at: string }[];
};
export type MbaOwnership = {
  recovery_at?: string;
  incidents: { id: number; source: string; observed_at: string }[];
  threads: { id: number; subscriber_id: number; owner: string; source: string; observed_at: string; subscriber?: { first_name?: string; sender_id?: string } }[];
};

const url = (bot: MbaBotCredentials, path: string) => {
  const params = new URLSearchParams({ key: bot.key || "", password: bot.password || "" });
  return `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${bot.id}/meta-business-agent/${path}?${params}`;
};

async function call<T>(bot: MbaBotCredentials, path: string, options: RequestInit = {}, authenticated = false): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
  if (authenticated) headers.Authorization = `Bearer ${await auth.getBearerToken()}`;
  const response = await fetch(url(bot, path), { ...options, headers: { ...headers, ...(options.headers || {}) } });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const validation = body?.errors && Object.values(body.errors).flat().find(value => typeof value === "string");
    throw new Error(body?.error?.message || validation || body?.message || body?.error?.code || `MBA request failed (${response.status})`);
  }
  if (!body || !("data" in body)) throw new Error("MBA returned an unexpected response. Refresh and try again.");
  return body.data as T;
}

export const getMbaReadiness = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaReadiness>(bot, "readiness", { signal });
export const getMbaKnowledge = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaKnowledge>(bot, "knowledge", { signal });
export const getMbaSettings = async (bot: MbaBotCredentials, signal?: AbortSignal) => {
  const result = await call<MbaSettings | MbaSettings[]>(bot, "settings", { signal });
  return Array.isArray(result) ? result[0] : result;
};
export const getMbaAllowlist = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaAllowlistEntry[]>(bot, "allowlist", { signal }, true);
export const getMbaManagementAccess = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaManagementAccess>(bot, "management-access", { signal }, true);
export const getMbaBusinessInfo = async (bot: MbaBotCredentials, signal?: AbortSignal) => {
  const result = await call<MbaBusinessInfo | MbaBusinessInfo[]>(bot, "business-info", { signal }, true);
  return Array.isArray(result) ? result[0] || {} : result;
};
export const getMbaWebsites = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaWebsite[]>(bot, "websites", { signal }, true);
export const getMbaFiles = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaFile[]>(bot, "files", { signal }, true);
export const getMbaSkills = async (bot: MbaBotCredentials, signal?: AbortSignal) => {
  const result = await call<MbaSkill[] | { data: MbaSkill[] }>(bot, "skills", { signal }, true);
  return Array.isArray(result) ? result : result.data || [];
};
const write = <T>(bot: MbaBotCredentials, path: string, method: string, body?: unknown) =>
  call<T>(bot, path, { method, body: body === undefined ? undefined : JSON.stringify(body) }, true);
export const getMbaFaqSync = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaFaqSync>(bot, "knowledge/sync", { signal }, true);
export const setMbaFaqSync = (bot: MbaBotCredentials, enabled: boolean) => write<{ enabled: boolean }>(bot, "knowledge/sync", "PUT", { enabled, confirmed: true });
export const retryMbaFaqSync = (bot: MbaBotCredentials, sourceIds: string[]) => write<{ queued: number }>(bot, "knowledge/sync/retry", "POST", { source_ids: sourceIds, confirmed: true });
export const getMbaOwnership = (bot: MbaBotCredentials, signal?: AbortSignal) => call<MbaOwnership>(bot, "ownership", { signal }, true);
export const recoverMbaOwnership = (bot: MbaBotCredentials, incidentIds: number[]) => write<{ resolved: number }>(bot, "ownership/recover", "POST", { incident_ids: incidentIds, confirmed: true });
export const releaseMbaThread = (bot: MbaBotCredentials, subscriberId: number) => write<unknown>(bot, `chats/${subscriberId}/release`, "POST", { confirmed: true });
export const onboardMba = (bot: MbaBotCredentials) => write<{ agent_id?: string; feature_enabled: false }>(bot, "onboard", "POST", { confirmed: true });
export const updateMbaSettings = (bot: MbaBotCredentials, settings: Partial<MbaSettings>) => write<MbaSettings | MbaSettings[]>(bot, "settings", "PUT", settings);
export const testMbaAgent = (bot: MbaBotCredentials, message: string, conversationId?: string) => write<MbaAgentTestResult>(bot, "test", "POST", { message, conversation_id: conversationId });
export const previewMbaKnowledge = (bot: MbaBotCredentials) => write<MbaKnowledgePreview>(bot, "knowledge/preview", "POST");
export const publishMbaKnowledge = (bot: MbaBotCredentials, sourceIds: string[]) => write<Record<string, number>>(bot, "knowledge/publish", "POST", { confirmed: true, source_ids: sourceIds });
export const updateMbaBusinessInfo = async (bot: MbaBotCredentials, data: MbaBusinessInfo) => {
  const result = await write<MbaBusinessInfo | MbaBusinessInfo[]>(bot, "business-info", "PUT", data);
  return Array.isArray(result) ? result[0] || {} : result;
};
export const resetMbaBusinessInfo = (bot: MbaBotCredentials) => write<Record<string, unknown>>(bot, "business-info", "DELETE", { confirmed: true });
export const addMbaWebsite = (bot: MbaBotCredentials, websiteUrl: string) => write<MbaWebsite>(bot, "websites", "POST", { url: websiteUrl });
export const removeMbaWebsite = (bot: MbaBotCredentials, websiteId: string) => write<Record<string, unknown>>(bot, `websites/${encodeURIComponent(websiteId)}`, "DELETE", { confirmed: true });
export const createMbaSkill = (bot: MbaBotCredentials, skill: MbaSkillInput) => write<MbaSkill>(bot, "skills", "POST", skill);
export const updateMbaSkill = (bot: MbaBotCredentials, skillId: string, changes: Pick<MbaSkill, "title" | "status" | "instruction">) => write<MbaSkill>(bot, `skills/${encodeURIComponent(skillId)}`, "PUT", changes);
export const deleteMbaSkill = (bot: MbaBotCredentials, skillId: string) => write<Record<string, unknown>>(bot, `skills/${encodeURIComponent(skillId)}`, "DELETE", { confirmed: true });
export const addMbaAllowlist = (bot: MbaBotCredentials, phoneNumber: string) => write<MbaAllowlistEntry>(bot, "allowlist", "POST", { phone_number: phoneNumber });
export const removeMbaAllowlist = (bot: MbaBotCredentials, entryId: string) => write<Record<string, unknown>>(bot, `allowlist/${encodeURIComponent(entryId)}`, "DELETE");
export const setMbaRollout = (bot: MbaBotCredentials, audience: "ALLOWLISTED_ONLY" | "EVERYONE" | "DISABLED") =>
  write<MbaSettings | MbaSettings[]>(
    bot,
    audience === "ALLOWLISTED_ONLY" ? "rollout/allowlist" : audience === "EVERYONE" ? "rollout/everyone" : "rollout/disable",
    "POST",
    audience === "EVERYONE" ? { confirmed: true, billing_acknowledged: true } : { confirmed: true }
  );
